import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ExternalLink, FileText, Image as ImageIcon, Mail, Linkedin, Youtube, Instagram, Globe, ArrowUpRight, X, Eye } from "lucide-react";
import { api, resolveUrl as mediaUrl } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const YT = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/;
const VIMEO = /vimeo\.com\/(\d+)/;
const CAT_FALLBACK = {
  sports: "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "motion graphics": "https://images.unsplash.com/photo-1618386230353-3631c1365be2?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  youtube: "https://images.unsplash.com/photo-1626218174358-7769486c4b79?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
};
const DEFAULT_THUMB = "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

const embedUrl = (u) => {
  if (!u) return null;
  let m = u.match(YT); if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
  m = u.match(VIMEO); if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
  return null;
};
const thumbFor = (it) => mediaUrl(it.thumbnail) || CAT_FALLBACK[(it.category || "").toLowerCase()] || DEFAULT_THUMB;

const reveal = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };

export default function Portfolio() {
  const [sp] = useSearchParams();
  const { slug } = useParams();
  const preview = sp.get("preview") === "1" || sp.get("preview") === "true";
  const [data, setData] = useState(null);
  const [active, setActive] = useState("All");
  const [modal, setModal] = useState(null); // item being viewed (video/image)
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    setLoading(true); setUnavailable(false);
    api.portfolioBySlug(slug, preview)
      .then(setData)
      .catch(() => setUnavailable(true))
      .finally(() => setLoading(false));
  }, [slug, preview]);

  const s = data?.settings || {};
  const items = data?.items || [];

  // SEO — dynamic title/meta/OG from the portfolio owner.
  useEffect(() => {
    if (!s) return;
    const title = s.name ? `${s.name} — ${s.title || "Portfolio"}` : "Portfolio";
    document.title = title;
    const set = (sel, attr, val) => { let el = document.head.querySelector(sel); if (!el) { el = document.createElement("meta"); const [a, v] = sel.replace(/[[\]']/g, "").split("="); el.setAttribute(a === "name" ? "name" : "property", v); document.head.appendChild(el); } el.setAttribute("content", val || ""); };
    set("meta[name='description']", "content", s.bio || "Professional video editor portfolio.");
    set("meta[property='og:title']", "content", title);
    set("meta[property='og:description']", "content", s.bio || "");
    if (s.profile_image || s.hero_image) set("meta[property='og:image']", "content", mediaUrl(s.hero_image || s.profile_image));
  }, [s]);

  const cats = useMemo(() => {
    const used = new Set(items.map((i) => i.category).filter(Boolean));
    const ordered = (data?.categories || []).map((c) => c.name).filter((n) => used.has(n));
    return ["All", ...ordered];
  }, [items, data]);

  const shown = active === "All" ? items : items.filter((i) => i.category === active);

  const openItem = (it) => {
    api.portfolioView(it.id);
    if (it.file_type === "video") { setModal(it); return; }
    if (it.file_type === "image") { setModal(it); return; }
    // pdf / document / link / website → open in a new tab
    const url = mediaUrl(it.media_url) || it.external_url;
    if (url) { api.portfolioClick(it.id); window.open(url, "_blank", "noopener,noreferrer"); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#EAB308] border-t-transparent" /></div>;
  if (unavailable) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0A] px-6 text-center font-ui text-white" data-testid="portfolio-unavailable">
      <h1 className="font-display text-4xl font-black">Portfolio unavailable</h1>
      <p className="mt-3 text-[#A1A1AA]">This portfolio doesn't exist or isn't published yet.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] font-ui text-white antialiased" data-testid="portfolio-page"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E\")" }}>
      {preview && data?.preview && <div className="bg-[#EAB308] py-2 text-center text-xs font-bold uppercase tracking-widest text-black" data-testid="portfolio-preview-banner">Preview mode — showing unpublished items too</div>}

      {/* Sticky glass nav */}
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="font-display text-lg font-black tracking-tighter">{s.name || "Portfolio"}</span>
          <div className="flex items-center gap-6 text-sm">
            <a href="#work" className="hidden text-[#A1A1AA] hover:text-white sm:block">Work</a>
            <a href="#about" className="hidden text-[#A1A1AA] hover:text-white sm:block">About</a>
            <a href="#contact" data-testid="nav-contact" className="rounded-none bg-[#EAB308] px-5 py-2 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-white">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-[#EAB308]/10 blur-[120px]" />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-24 lg:grid-cols-[1.4fr_1fr] lg:py-32">
          <motion.div initial="hidden" animate="show" variants={reveal}>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[#EAB308]" data-testid="hero-eyebrow">{s.title || "Video Editor"}</p>
            <h1 className="font-display text-6xl font-black leading-[0.9] -tracking-[0.03em] sm:text-7xl lg:text-8xl" data-testid="portfolio-name">{s.name || "Your Name"}</h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A1A1AA]">{s.bio || "Professional video editor. Add your bio from the admin dashboard."}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#work" data-testid="cta-view-work" className="bg-[#EAB308] px-8 py-4 text-sm font-bold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-white">View My Work</a>
              <a href="#contact" data-testid="cta-contact" className="border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-[#EAB308] hover:text-[#EAB308]">Contact Me</a>
            </div>
          </motion.div>
          {(s.profile_image || s.hero_image) && (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
              <div className="relative overflow-hidden rounded-2xl border border-white/10">
                <img src={mediaUrl(s.hero_image || s.profile_image)} alt={s.name || "Profile"} loading="lazy" className="aspect-[4/5] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              </div>
            </motion.div>
          )}
        </div>
      </header>

      {/* About */}
      <section id="about" className="border-t border-white/5 py-24">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-display text-4xl font-black -tracking-[0.02em] sm:text-5xl">About</h2>
          <p className="mt-8 whitespace-pre-line text-lg leading-relaxed text-[#A1A1AA]" data-testid="about-text">{s.long_description || s.bio || "Add your story from the admin dashboard."}</p>
        </div>
      </section>

      {/* Featured Work */}
      <section id="work" className="border-t border-white/5 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-display text-4xl font-black -tracking-[0.02em] sm:text-5xl">Featured Work</h2>

          {cats.length > 1 && (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="portfolio-filters">
              {cats.map((c) => (
                <button key={c} onClick={() => setActive(c)} data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-6 py-2 text-sm transition-colors ${active === c ? "bg-[#EAB308] font-bold text-black" : "border border-[#27272A] text-[#A1A1AA] hover:border-white/30 hover:text-white"}`}>{c}</button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <div className="mt-16 rounded-2xl border border-dashed border-white/10 py-20 text-center text-[#71717A]" data-testid="portfolio-empty">No projects to show yet.</div>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
              {shown.map((it, i) => {
                const big = i % 5 === 0;
                return (
                  <motion.div key={it.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={reveal}
                    className={big ? "md:col-span-8" : "md:col-span-4"}>
                    <ProjectCard it={it} onOpen={() => openItem(it)} tall={big} />
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-white/5 py-24">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-display text-4xl font-black -tracking-[0.02em] sm:text-5xl">Let's work together</h2>
          {s.email && <a href={`mailto:${s.email}`} data-testid="contact-email" className="mt-8 inline-flex items-center gap-2 bg-[#EAB308] px-8 py-4 text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-white"><Mail size={16} /> {s.email}</a>}
          <div className="mt-10 flex items-center justify-center gap-4">
            {[["linkedin", s.linkedin, Linkedin], ["youtube", s.youtube, Youtube], ["instagram", s.instagram, Instagram], ["website", s.website, Globe]].map(([k, url, Icon]) => url ? (
              <a key={k} href={url} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#A1A1AA] transition-colors hover:border-[#EAB308] hover:text-[#EAB308]"><Icon size={18} /></a>
            ) : null)}
            {(s.other_links || []).map((l, i) => l.url ? (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="flex h-12 items-center gap-1 rounded-full border border-white/15 px-4 text-sm text-[#A1A1AA] transition-colors hover:border-[#EAB308] hover:text-[#EAB308]">{l.label || "Link"} <ArrowUpRight size={14} /></a>
            ) : null)}
          </div>
        </div>
        <footer className="mt-16 text-center text-xs text-[#52525B]">© {new Date().getFullYear()} {s.name || "Portfolio"}. Built with CaptionMotion.</footer>
      </section>

      {/* Media modal (video / image) */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-4xl border-none bg-transparent p-0 shadow-none" data-testid="portfolio-modal">
          {modal && <ModalBody it={modal} onClose={() => setModal(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TYPE_ICON = { pdf: FileText, document: FileText, image: ImageIcon, link: ExternalLink, website: Globe, video: Play };

const ProjectCard = ({ it, onOpen, tall }) => {
  const Icon = TYPE_ICON[it.file_type] || Play;
  return (
    <button onClick={onOpen} data-testid={`portfolio-card-${it.id}`} className="group block w-full text-left">
      <div className={`relative overflow-hidden rounded-xl border border-white/5 bg-[#121212] ${tall ? "aspect-video" : "aspect-[4/3]"}`}>
        <img src={thumbFor(it)} alt={it.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-80" />
        <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors group-hover:bg-[#EAB308] group-hover:text-black"><Icon size={15} /></div>
        {!it.is_published && <span className="absolute left-3 top-3 rounded bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#EAB308]">Draft</span>}
        {(it.views > 0) && <span className="absolute bottom-3 left-3 flex items-center gap-1 text-[11px] text-white/70"><Eye size={11} /> {it.views}</span>}
      </div>
      <div className="mt-4">
        {it.category && <div className="text-xs font-bold uppercase tracking-widest text-[#EAB308]">{it.category}</div>}
        <div className="mt-1 text-xl font-bold text-white">{it.title}</div>
        {it.description && <p className="mt-1 line-clamp-2 text-sm text-[#A1A1AA]">{it.description}</p>}
      </div>
    </button>
  );
};

const ModalBody = ({ it, onClose }) => {
  const closed = useRef(false);
  useEffect(() => { if (!closed.current) { api.portfolioClick(it.id); closed.current = true; } }, [it.id]);
  const embed = it.file_type === "video" ? embedUrl(it.external_url) : null;
  const localVid = it.file_type === "video" && !embed ? mediaUrl(it.media_url) : null;
  return (
    <div className="relative">
      <button onClick={onClose} data-testid="modal-close" className="absolute -top-10 right-0 text-white/70 hover:text-white"><X size={22} /></button>
      {it.file_type === "image" ? (
        <img src={mediaUrl(it.media_url) || thumbFor(it)} alt={it.title} className="max-h-[80vh] w-full rounded-lg object-contain" data-testid="modal-image" />
      ) : embed ? (
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-black"><iframe src={embed} title={it.title} className="h-full w-full" allow="autoplay; fullscreen" allowFullScreen /></div>
      ) : localVid ? (
        <video src={localVid} controls autoPlay className="max-h-[80vh] w-full rounded-lg bg-black" data-testid="modal-video" />
      ) : (
        <div className="rounded-lg bg-[#121212] p-10 text-center text-[#A1A1AA]">No playable media. <a href={it.external_url || "#"} target="_blank" rel="noopener noreferrer" className="text-[#EAB308] underline">Open link</a></div>
      )}
      <div className="mt-4 text-center"><div className="text-lg font-bold text-white">{it.title}</div>{it.description && <p className="mt-1 text-sm text-[#A1A1AA]">{it.description}</p>}</div>
    </div>
  );
};
